import { useEffect, useMemo, useRef, useState } from "react";
import { ArrowLeft, ArrowRight, CheckCircle2, RefreshCw } from "lucide-react";
import { useAppDispatch } from "../../../store/hooks";
import { setSelectedSkuIds } from "../../../store/slices/workflowSlice";
import { fetchRecordByKey } from "../../../services/IndexedDBUtil";
import { DATABASE_NAME, STORE_NAME } from "../../../services/constants";
import { useFormContext } from "../../createworkflow/FormContext";
import {
  // useFeatureFiltersQuery, // legacy cascading-filter flow — commented out, see DynamicFilters below
  // useMatchingSkusQuery, // legacy cascading-filter flow — commented out, see DynamicFilters below
  useAllFeatureCombinationsQuery,
  useProductTypesQuery,
} from "./hooks/useFeatureForecastingQueries";
import {
  getFeatureSkuId,
  normalizeFeatureFiltersForUi,
  normalizeFeatureSkuSelectionForStage3,
  normalizeSelectedFiltersForApi,
  // pruneSelectedFilters, // only used by the legacy filter-pruning effect below
} from "./utils/normalizers";
import { useDebounce } from "./hooks/useDebounce";
// import DynamicFilters from "./components/DynamicFilters"; // legacy filter UI — commented out per request, kept for later use
import FeatureCombinationsTable from "./components/FeatureCombinationsTable";
import ProductTypeSelect from "./components/ProductTypeSelect";
import StageHeader from "./components/StageHeader";
import StatusMessage from "./components/StatusMessage";
import "./styles.css";

const Stage3FeatureLevelForecasting = ({ onBack, onNext }) => {
  const dispatch = useAppDispatch();
  const { formData, updateFormData } = useFormContext();
  const dataUploadId = formData?.stage1?.data_upload_id;

  const [productType, setProductType] = useState("");
  const [selectedFilters, setSelectedFilters] = useState({});
  const [selectedSkusById, setSelectedSkusById] = useState({});
  const [selectedCombinationIds, setSelectedCombinationIds] = useState({});
  const [expandedCombinationIds, setExpandedCombinationIds] = useState({});
  const [validationMessage, setValidationMessage] = useState("");
  const [isProceeding, setIsProceeding] = useState(false);

  // Prevents the debounce-driven SKU clear from firing immediately after a restore
  const isRestoringRef = useRef(false);

  // Auto-cascade: debounce selectedFilters → apiSelectedFilters → react-query fires
  const debouncedSelectedFilters = useDebounce(selectedFilters, 400);
  const apiSelectedFilters = useMemo(
    () => normalizeSelectedFiltersForApi(debouncedSelectedFilters),
    [debouncedSelectedFilters]
  );

  const productTypesQuery = useProductTypesQuery(dataUploadId);
  // Legacy cascading-filter queries — commented out per request, kept for later use
  // const filtersQuery = useFeatureFiltersQuery({
  //   dataUploadId,
  //   productType,
  //   selectedFilters: apiSelectedFilters,
  // });
  // const skusQuery = useMatchingSkusQuery({
  //   dataUploadId,
  //   productType,
  //   filters: apiSelectedFilters,
  // });
  // const availableFilters = filtersQuery.data?.filters || {};
  // const skuCount = filtersQuery.data?.skuCount ?? skusQuery.data?.total;

  const combinationsQuery = useAllFeatureCombinationsQuery({
    dataUploadId,
    productType,
    baseFilters: {},
  });
  const combinations = combinationsQuery.data?.combinations || [];
  const totalCombinations = combinationsQuery.data?.total ?? 0;

  const selectedSkuIds = useMemo(() => new Set(Object.keys(selectedSkusById)), [selectedSkusById]);

  // Restore saved selection when component mounts or formData changes
  useEffect(() => {
    const restored = formData?.stage3?.featureBasedForecasting;
    if (!restored?.selected_skus?.length && !restored?.selected_combinations?.length) return;

    // Guard: prevent the debounce effect from clearing the restored SKUs
    isRestoringRef.current = true;
    const guardTimer = setTimeout(() => { isRestoringRef.current = false; }, 700);

    const restoredSkus = restored.selected_skus.map((sku) => {
      // If returning from a fully saved state (via Next), the raw SKU is inside `featureSku`
      const raw = sku.featureSku || sku;
      return {
        ...raw,
        _rowId: String(raw._rowId || getFeatureSkuId(raw) || raw._skuCode || sku._rowId || ""),
        _skuCode: raw._skuCode || raw.sku_code || raw.skuCode || raw._rowId || "-",
        _description: raw._description || raw.sku_name || raw.skuDescription || raw.description || "-",
      };
    });

    const normalizedFilters = normalizeFeatureFiltersForUi(
      restored.selected_filters_ui || restored.selected_filters
    );
    setProductType(restored.product_type || "");
    setSelectedFilters(normalizedFilters);
    setSelectedSkusById(Object.fromEntries(restoredSkus.map((sku) => [sku._rowId, sku])));

    // Restore checked groups so refresh / page navigation keeps the selection
    const restoredCombos = restored.selected_combinations || [];
    setSelectedCombinationIds(
      Object.fromEntries(restoredCombos.map((combo) => [combo.combinationId, combo]))
    );

    return () => clearTimeout(guardTimer);
  }, [formData?.stage3?.featureBasedForecasting]);

  // When filters change (after debounce), clear validation messages.
  // We no longer clear SKU selection here so users can refine filters without losing selected SKUs.
  useEffect(() => {
    if (isRestoringRef.current) return;
    setValidationMessage("");
  }, [debouncedSelectedFilters]);

  // Legacy cascading-filter effects — commented out per request, kept for later use
  // // Prune selected filters if the API removes previously valid option values
  // useEffect(() => {
  //   if (!filtersQuery.data?.filters) return;
  //   const available = filtersQuery.data.filters;
  //   setSelectedFilters((current) => {
  //     const pruned = pruneSelectedFilters(current, available);
  //     if (JSON.stringify(pruned) !== JSON.stringify(current)) {
  //       setTimeout(() => syncDraft(productType, pruned, selectedSkusById), 0);
  //       return pruned;
  //     }
  //     return current;
  //   });
  // }, [filtersQuery.data?.filters]);
  //
  // // Auto-select all matched SKUs under the hood
  // useEffect(() => {
  //   if (skusQuery.isSuccess && skusQuery.data?.skus) {
  //     const nextSkusById = Object.fromEntries(
  //       skusQuery.data.skus.map((sku) => [sku._rowId, sku])
  //     );
  //     setSelectedSkusById(nextSkusById);
  //     syncDraft(productType, selectedFilters, nextSkusById);
  //     setValidationMessage("");
  //   }
  // }, [skusQuery.data?.skus, skusQuery.isSuccess]);

  // Helper to persist draft state so a browser refresh doesn't lose work.
  // Selected combinations (groups) are persisted too so the checked groups
  // survive refresh and page navigation.
  const syncDraft = (nextType, nextFilters, nextSkusById, nextCombosById = selectedCombinationIds) => {
    const draftSkus = Object.values(nextSkusById).map(sku => ({
      ...sku,
      _rowId: String(sku._rowId || getFeatureSkuId(sku) || sku._skuCode || ""),
      _skuCode: sku._skuCode || sku.sku_code || sku.skuCode || sku._rowId || "-",
      _description: sku._description || sku.sku_name || sku.skuDescription || sku.description || "-",
    }));

    const draftCombos = Object.values(nextCombosById || {}).map((combo) => ({
      combinationId: combo.combinationId,
      groupLabel: combo.groupLabel,
      combination: combo.combination,
      skuCount: combo.skuCount,
      skus: combo.skus,
    }));

    updateFormData("stage3", {
      ...formData?.stage3,
      featureBasedForecasting: {
        product_type: nextType,
        selected_filters_ui: nextFilters,
        selected_skus: draftSkus,
        selected_combinations: draftCombos,
      },
      // Keep products empty to signal Stage 3 is not finalized yet
      products: [],
      geography: formData?.stage3?.geography || []
    });
  };

  const handleProductTypeChange = (nextProductType) => {
    setProductType(nextProductType);
    setSelectedFilters({});
    setSelectedSkusById({});
    setSelectedCombinationIds({});
    setExpandedCombinationIds({});
    setValidationMessage("");
    syncDraft(nextProductType, {}, {}, {});
  };

  // Legacy cascading-filter handlers — commented out per request, kept for later use
  // const handleFilterChange = (filterName, values) => {
  //   setSelectedFilters((current) => {
  //     const next = { ...current };
  //     if (values.length) {
  //       next[filterName] = values;
  //     } else {
  //       delete next[filterName];
  //     }
  //     syncDraft(productType, next, selectedSkusById);
  //     return next;
  //   });
  // };
  //
  // const handleClearFilters = () => {
  //   setSelectedFilters({});
  //   setSelectedSkusById({});
  //   setValidationMessage("");
  //   syncDraft(productType, {}, {});
  // };

  const handleToggleCombination = (combo) => {
    setSelectedCombinationIds((current) => {
      const next = { ...current };
      if (next[combo.combinationId]) {
        delete next[combo.combinationId];
      } else {
        next[combo.combinationId] = combo;
      }

      const nextSkusById = Object.values(next).reduce((acc, selectedCombo) => {
        selectedCombo.skus.forEach((sku) => {
          acc[sku._rowId] = sku;
        });
        return acc;
      }, {});
      setSelectedSkusById(nextSkusById);
      syncDraft(productType, selectedFilters, nextSkusById, next);
      return next;
    });
    setValidationMessage("");
  };

  const handleToggleAllCombinations = (visibleCombos, allVisibleSelected) => {
    setSelectedCombinationIds((current) => {
      const next = { ...current };
      visibleCombos.forEach((combo) => {
        if (allVisibleSelected) {
          delete next[combo.combinationId];
        } else {
          next[combo.combinationId] = combo;
        }
      });

      const nextSkusById = Object.values(next).reduce((acc, selectedCombo) => {
        selectedCombo.skus.forEach((sku) => {
          acc[sku._rowId] = sku;
        });
        return acc;
      }, {});
      setSelectedSkusById(nextSkusById);
      syncDraft(productType, selectedFilters, nextSkusById, next);
      return next;
    });
    setValidationMessage("");
  };

  const handleToggleCombinationDetails = (combinationId) => {
    setExpandedCombinationIds((current) => ({
      ...current,
      [combinationId]: !current[combinationId],
    }));
  };

  const handleToggleSku = (sku) => {
    setSelectedSkusById((current) => {
      const next = { ...current };
      if (next[sku._rowId]) {
        delete next[sku._rowId];
      } else {
        next[sku._rowId] = sku;
      }
      syncDraft(productType, selectedFilters, next);
      return next;
    });
    setValidationMessage("");
  };

  const handleToggleAll = (visibleSkus, allVisibleSelected) => {
    setSelectedSkusById((current) => {
      const next = { ...current };
      visibleSkus.forEach((sku) => {
        if (allVisibleSelected) {
          delete next[sku._rowId];
        } else {
          next[sku._rowId] = sku;
        }
      });
      syncDraft(productType, selectedFilters, next);
      return next;
    });
    setValidationMessage("");
  };

  const selectedSkus = useMemo(() => Object.values(selectedSkusById), [selectedSkusById]);

  const handleNext = async () => {
    if (!selectedSkus.length) {
      setValidationMessage("No SKUs match the selected filters. Please adjust your selection.");
      return;
    }

    setIsProceeding(true);
    try {
      const hierarchyResult = await fetchRecordByKey(DATABASE_NAME, STORE_NAME, "SKUS");
      const hierarchySkus =
        hierarchyResult.success && Array.isArray(hierarchyResult.record.data)
          ? hierarchyResult.record.data
          : [];

      const selectedProducts = normalizeFeatureSkuSelectionForStage3(
        selectedSkus,
        productType,
        hierarchySkus
      );

      const featureSelectionSkus = selectedProducts.map((product) => ({
        ...product,
        _rowId: product.id,
        _skuCode: product.sku_code || product.breadcrumb?.split("\\").pop(),
        _description: product.sku_description || product.breadcrumb?.split("\\").pop(),
      }));

      const featureSelection = {
        product_type: productType,
        selected_filters: apiSelectedFilters,
        selected_filters_ui: selectedFilters,
        selected_skus: featureSelectionSkus,
        selected_combinations: Object.values(selectedCombinationIds).map((combo) => ({
          combinationId: combo.combinationId,
          groupLabel: combo.groupLabel,
          combination: combo.combination,
          skuCount: combo.skuCount,
          skus: combo.skus,
        })),
      };

      const standardProducts = selectedProducts.map((p) => ({ id: p.id, breadcrumb: p.breadcrumb }));
      const existingGeography = formData?.stage3?.geography || [];

      updateFormData("stage3", {
        products: standardProducts,
        geography: existingGeography,
        featureBasedForecasting: featureSelection,
      });
      updateFormData("stage4", {
        combinations: [],
        featureSelectedSkus: featureSelectionSkus,
        featureSelectionApplied: false,
      });

      dispatch(setSelectedSkuIds(featureSelectionSkus.map((sku) => sku.sku_id || sku._rowId).filter(Boolean)));

      if (onNext) {
        onNext();
      } else if (onBack) {
        onBack();
      }
    } catch (error) {
      console.error("Error persisting feature level SKU selection:", error);
      setValidationMessage("Unable to save selected SKUs. Please try again.");
    } finally {
      setIsProceeding(false);
    }
  };

  const hasNoDataUpload = !dataUploadId;

  return (
    <div className="feature-forecasting-wrapper">
      <div className="feature-forecasting-card">
        <StageHeader />

        {/* ── Compact selection badge ── */}
        <div className="selection-badge">
          <div className="selection-badge__left">
            <CheckCircle2 size={15} className="selection-badge__icon" />
            <span>
              Selected:&nbsp;<strong>Feature Based Forecasting</strong>
            </span>
          </div>
          <button className="selection-badge__change" onClick={onBack} type="button">
            <RefreshCw size={13} />
            Change Selection
          </button>
        </div>

        {hasNoDataUpload ? (
          <StatusMessage tone="danger" title="Dataset required">
            Please complete Stage 1 before configuring Feature Based Forecasting.
          </StatusMessage>
        ) : (
          <>
            <ProductTypeSelect
              options={productTypesQuery.data || []}
              value={productType}
              onChange={handleProductTypeChange}
              isLoading={productTypesQuery.isLoading}
              isDisabled={productTypesQuery.isError}
            />

            {productTypesQuery.isError && (
              <StatusMessage tone="danger" title="Unable to load product types">
                {productTypesQuery.error?.message || "Please try again."}
              </StatusMessage>
            )}

            {!productTypesQuery.isLoading && !productTypesQuery.isError && !productType && (
              <StatusMessage tone="info" title="Start with Product Type">
                Feature filters will appear after a product type is selected.
              </StatusMessage>
            )}

            {productType && (
              <>
                {/* Legacy cascading Feature Filters UI — commented out per request, kept for later use
                <DynamicFilters
                  filters={availableFilters}
                  selectedFilters={selectedFilters}
                  skuCount={skuCount}
                  isLoading={filtersQuery.isLoading}
                  isFetching={filtersQuery.isFetching}
                  error={filtersQuery.error}
                  onFilterChange={handleFilterChange}
                  onClearFilters={handleClearFilters}
                />
                */}
                <FeatureCombinationsTable
                  combinations={combinations}
                  productType={productType}
                  total={totalCombinations}
                  isLoading={combinationsQuery.isLoading}
                  isFetching={combinationsQuery.isFetching}
                  error={combinationsQuery.error}
                  selectedCombinationIds={selectedCombinationIds}
                  expandedCombinationIds={expandedCombinationIds}
                  onToggleCombination={handleToggleCombination}
                  onToggleAllCombinations={handleToggleAllCombinations}
                  onToggleDetails={handleToggleCombinationDetails}
                />
              </>
            )}
          </>
        )}

        {validationMessage && (
          <StatusMessage tone="danger" title={validationMessage} />
        )}

        <div className="feature-actions">
          <button type="button" className="feature-btn feature-btn--secondary" onClick={onBack}>
            <ArrowLeft size={18} />
            Back
          </button>
          <button
            type="button"
            className="feature-btn feature-btn--primary"
            onClick={handleNext}
            disabled={hasNoDataUpload || !selectedSkus.length || isProceeding}
          >
            {isProceeding ? "Moving…" : "Next Step"}
            <ArrowRight size={18} />
          </button>
        </div>
      </div>
    </div>
  );
};

export default Stage3FeatureLevelForecasting;
